import { useState, useEffect } from 'react';
import { ExternalLink, Flame, TrendingUp, Heart, Tag, ChevronRight } from 'lucide-react';
import { isInWishlist, toggleWishlistItem } from '@/pages/Favoritos';

const platformStyles = {
  shopee: { bg: 'bg-[#EE4D2D]', text: 'text-white', label: 'Shopee', buttonText: 'VER OFERTA' },
  mercadolivre: { bg: 'bg-[#FFE600]', text: 'text-black', label: 'Mercado Livre', buttonText: 'VER OFERTA' },
  amazon: { bg: 'bg-[#FF9900]', text: 'text-black', label: 'Amazon', buttonText: 'VER OFERTA' },
  temu: { bg: 'bg-[#FB7701]', text: 'text-white', label: 'Temu', buttonText: 'VER OFERTA' },
  hotmart: { bg: 'bg-[#F04E23]', text: 'text-white', label: 'Hotmart', buttonText: 'VER OFERTA' },
  shein: { bg: 'bg-[#000000]', text: 'text-white', label: 'Shein', buttonText: 'VER OFERTA' },
  kabum: { bg: 'bg-[#FF6500]', text: 'text-white', label: 'Kabum', buttonText: 'VER OFERTA' },
  casasbahia: { bg: 'bg-[#0066CC]', text: 'text-white', label: 'Casas Bahia', buttonText: 'VER OFERTA' },
  americanas: { bg: 'bg-[#E60014]', text: 'text-white', label: 'Americanas', buttonText: 'VER OFERTA' },
};

const badgeTypes = [
  { label: 'OFERTA UNICA', bg: 'bg-red-600' },
  { label: 'VIRAL', bg: 'bg-purple-600' },
  { label: 'DESTAQUE', bg: 'bg-yellow-500' },
];

const getRandomBadge = (productId) => {
  if (!productId) return null;
  const hash = productId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  if (hash % 3 === 0) return badgeTypes[0];
  if (hash % 5 === 0) return badgeTypes[1];
  if (hash % 7 === 0) return badgeTypes[2];
  return null;
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

export const ProductCard = ({ product, compact = false, mobileList = false, animDelay = 0 }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const platform = platformStyles[product.platform] || { bg: 'bg-slate-600', text: 'text-white', label: product.platform, buttonText: 'VER OFERTA' };

  const isBestseller = product.is_viral || product.click_count > 10;
  const extraBadge = getRandomBadge(product.id);

  // Fix: Calculate discount automatically - hide tiny discounts (< 5%)
  const rawDiscount = (product.old_price && product.old_price > product.current_price)
    ? Math.round(((product.old_price - product.current_price) / product.old_price) * 100)
    : 0;
  const discountPercent = rawDiscount >= 5 ? rawDiscount : 0;
  const hasDiscount = discountPercent > 0;

  useEffect(() => {
    setWishlisted(isInWishlist(product.id));
    const handler = () => setWishlisted(isInWishlist(product.id));
    window.addEventListener('wishlist-updated', handler);
    return () => window.removeEventListener('wishlist-updated', handler);
  }, [product.id]);

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    toggleWishlistItem(product.id);
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Abrir link imediatamente para não bloquear popup no mobile
    const affiliateLink = product.affiliate_link;
    const newWindow = window.open(affiliateLink, '_blank', 'noopener,noreferrer');
    
    // Registrar clique em background (não bloqueia)
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      fetch(`${API_URL}/api/products/${product.id}/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }).catch(() => {}); // Ignora erros silenciosamente
    } catch (error) {
      // Ignora erros - o link já foi aberto
    }
    
    // Fallback para mobile se window.open não funcionou
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = affiliateLink;
    }
  };

  // ==================== MOBILE LIST LAYOUT ====================
  if (mobileList) {
    return (
      <div
        className="relative bg-white rounded-xl shadow-sm overflow-hidden flex gap-3 p-3"
        data-testid={`product-card-${product.id}`}
      >
        {/* Image */}
        <div className="relative w-28 h-28 flex-shrink-0 rounded-lg overflow-hidden bg-white">
          {!imageLoaded && <div className="absolute inset-0 skeleton" />}
          <img
            src={product.image_url}
            alt={product.name}
            className={`w-full h-full object-contain p-1 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
          />
          {hasDiscount && (
            <div className="absolute top-1 left-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
              -{discountPercent}%
            </div>
          )}
          <div className={`absolute bottom-1 left-1 ${platform.bg} ${platform.text} text-[9px] font-bold px-1.5 py-0.5 rounded`}>
            {platform.label}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 min-w-0 justify-between">
          <h3 className="text-sm font-medium text-slate-800 line-clamp-2 mb-1 pr-8" title={product.name}>
            {product.name}
          </h3>

          <div className="mb-2">
            {hasDiscount && (
              <p className="text-slate-400 line-through text-xs">De {formatPrice(product.old_price)}</p>
            )}
            <p className="text-[#FF4500] font-black text-xl font-heading">
              {formatPrice(product.current_price)}
            </p>
          </div>

          {/* Botão como link direto para melhor funcionamento no mobile */}
          <a
            href={product.affiliate_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="w-full bg-[#FF4500] hover:bg-[#E03D00] text-white font-bold py-2.5 text-xs rounded-lg btn-glow flex items-center justify-center gap-1.5 touch-manipulation"
            data-testid="product-cta-btn"
          >
            VER OFERTA
            <ChevronRight className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Wishlist */}
        <button
          onClick={handleWishlistToggle}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center touch-manipulation ${
            wishlisted ? 'bg-red-500 text-white' : 'bg-white/90 text-slate-400 shadow-sm'
          }`}
          data-testid="wishlist-toggle-btn"
          aria-label={wishlisted ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <Heart className={`w-4 h-4 ${wishlisted ? 'fill-white' : ''}`} />
        </button>
      </div>
    );
  }

  // ==================== CARD LAYOUT (Desktop / Grid) ====================
  return (
    <div
      className="group relative bg-white rounded-xl overflow-hidden flex flex-col shadow-[0_2px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 transition-all duration-300 h-full"
      data-testid={`product-card-${product.id}`}
    >
      {/* Discount Badge */}
      {hasDiscount && (
        <div
          className="absolute top-2 left-2 z-20 flex items-center gap-1 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-lg"
          data-testid="discount-badge"
        >
          <Flame className="w-3 h-3" />
          <span>-{discountPercent}%</span>
        </div>
      )}

      {/* Wishlist Heart Button */}
      <button
        onClick={handleWishlistToggle}
        className={`absolute top-2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
          wishlisted
            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
            : 'bg-white/80 backdrop-blur-sm text-slate-400 hover:text-red-500 hover:bg-white shadow-sm'
        }`}
        data-testid="wishlist-toggle-btn"
        aria-label={wishlisted ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
      >
        <Heart className={`w-4 h-4 ${wishlisted ? 'fill-white' : ''}`} />
      </button>

      {/* Bestseller Badge */}
      {isBestseller && !hasDiscount && (
        <div className="absolute top-2 left-2 z-20 flex items-center gap-1 bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded" data-testid="bestseller-badge">
          <TrendingUp className="w-2.5 h-2.5" />
          <span>VIRAL</span>
        </div>
      )}

      {/* Extra Colored Badge */}
      {extraBadge && !hasDiscount && !isBestseller && (
        <div className={`absolute top-2 left-2 z-20 flex items-center gap-1 ${extraBadge.bg} text-white text-[10px] font-bold px-2 py-0.5 rounded`} data-testid="extra-badge">
          <Tag className="w-2.5 h-2.5" />
          <span>{extraBadge.label}</span>
        </div>
      )}

      {/* Image Container */}
      <div className="relative w-full overflow-hidden bg-white" style={{ aspectRatio: '1/1' }}>
        {!imageLoaded && <div className="absolute inset-0 skeleton" />}
        <img
          src={product.image_url}
          alt={product.name}
          className={`w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
        />
        <div className={`absolute bottom-2 left-2 ${platform.bg} ${platform.text} text-[10px] font-bold px-2 py-1 rounded-md shadow-sm`}>
          {platform.label}
        </div>
      </div>

      {/* Content */}
      <div className={`flex flex-col flex-1 ${compact ? 'p-3' : 'p-3 md:p-4'}`}>
        <h3
          className={`font-medium text-slate-700 line-clamp-2 mb-2 group-hover:text-[#FF4500] transition-colors ${
            compact ? 'text-xs min-h-[32px]' : 'text-sm min-h-[40px]'
          }`}
          title={product.name}
        >
          {product.name}
        </h3>

        <div className="mb-3">
          {hasDiscount && (
            <p className="text-slate-400 line-through text-xs">
              De {formatPrice(product.old_price)}
            </p>
          )}
          <p className={`text-[#FF4500] font-black font-heading ${compact ? 'text-xl' : 'text-2xl'}`}>
            {formatPrice(product.current_price)}
          </p>
          {hasDiscount && (
            <p className="text-emerald-600 text-[10px] font-semibold mt-0.5">
              Economia de {formatPrice(product.old_price - product.current_price)}
            </p>
          )}
        </div>

        <div className="mt-auto">
          <a
            href={product.affiliate_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className={`w-full bg-[#FF4500] hover:bg-[#E03D00] text-white font-bold rounded-lg transition-all duration-300 active:scale-95 btn-glow flex items-center justify-center gap-1.5 touch-manipulation ${
              compact ? 'py-2.5 text-xs' : 'py-3 text-sm'
            }`}
            data-testid="product-cta-btn"
          >
            VER OFERTA
            <ChevronRight className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
